import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import type { AssetFormatProps, IAssetDisplay } from '@interxfi/design-system/components/format/asset';
import type { CurrencyFormatProps } from '@interxfi/design-system/components/format/currency';
import type { WellKnownDateFormatType } from '@interxfi/design-system/components/format/date';
import { ID, type IDProps } from '@interxfi/design-system/components/format/id';
import type { NumberFormatProps } from '@interxfi/design-system/components/format/number';
import { CodeEditorInput } from '@interxfi/design-system/components/input/code-editor-input/code-editor-input';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { defaultValueStackedTypographyProps } from '@interxfi/design-system/components/list/readonly/item/value';
import type { I18nNamespaces } from '@interxfi/translation';
import { type I18nKey, TranslatedText } from '@interxfi/translation/components/translated-text';

export type ReadonlyListItemOpts = Omit<ReadonlyListItem, 'id' | 'title' | 'value' | 'pill'>;

export function getReadonlyListItemString(
  id: string,
  label: ReactNode,
  value: string | undefined,
  to?: string,
  opts?: ReadonlyListItemOpts,
): ReadonlyListItem {
  return {
    id,
    title: label,
    value: { string: { value, to, LinkElement: Link } },
    ...opts,
  };
}

export function getReadonlyListItemId(
  id: string,
  label: ReactNode,
  value: string | undefined,
  to?: string,
  short = false,
  canCopy = true,
  opts?: ReadonlyListItemOpts,
): ReadonlyListItem {
  return {
    id,
    title: label,
    value: { id: { value, to, LinkElement: to ? Link : undefined, short, canCopy } },
    ...opts,
  };
}

export function getReadonlyListItemExternalId(
  id: string,
  label: ReactNode,
  value: string | undefined,
  to?: string,
  short = false,
  canCopy = true,
  opts?: ReadonlyListItemOpts,
): ReadonlyListItem {
  const base = getReadonlyListItemId(id, label, value, to, short, canCopy, opts);

  return {
    ...base,
    value: {
      ...base.value,
      id: {
        ...(base.value && 'id' in base.value ? base.value.id : {}),
        external: true,
        LinkElement: 'a',
      } as IDProps,
    },
  };
}

export function getReadonlyListItemYesOrNo(id: string, label: ReactNode, value: boolean | undefined, opts?: ReadonlyListItemOpts): ReadonlyListItem {
  return {
    id,
    title: label,
    value: { yesOrNo: { value } },
    ...opts,
  };
}

export function getReadonlyListItemNode(id: string, label: ReactNode, value: ReactNode | undefined, opts?: ReadonlyListItemOpts): ReadonlyListItem {
  return {
    id,
    title: label,
    value: { node: { value: value || null } },
    ...opts,
  };
}

export function getReadonlyListItemPill(id: string, label: ReactNode, value: ReactNode | undefined, opts?: ReadonlyListItemOpts): ReadonlyListItem {
  return {
    id,
    title: label,
    pill: value ?? undefined,
    value: value ? undefined : { string: { value: undefined } },
    ...opts,
  };
}

export function getReadonlyListItemSensitiveString(
  id: string,
  label: ReactNode,
  value: string | undefined,
  maskFn?: (v: string) => string,
  opts?: ReadonlyListItemOpts,
): ReadonlyListItem {
  return {
    id,
    title: label,
    value: { sensitiveString: { value, maskFn, buttonSize: 'small' } },
    ...opts,
  };
}

export function getReadonlyListItemDate(
  id: string,
  label: ReactNode,
  value: string | undefined,
  format: WellKnownDateFormatType = 'date_iso',
  opts?: ReadonlyListItemOpts & { timeZone?: string },
): ReadonlyListItem {
  return {
    id,
    title: label,
    value: { date: { value, wellKnownType: format, timeZone: opts?.timeZone } },
    ...opts,
  };
}

export function getReadonlyListItemNumber(
  id: string,
  label: ReactNode,
  value: number | string | undefined,
  formatOptions?: Omit<NumberFormatProps, 'value'>,
  opts?: ReadonlyListItemOpts,
): ReadonlyListItem {
  return {
    id,
    title: label,
    value: { number: { value, ...formatOptions } },
    ...opts,
  };
}

export function getReadonlyListItemCurrency(
  id: string,
  label: ReactNode,
  value: number | string | undefined,
  formatOptions?: Omit<CurrencyFormatProps, 'value'>,
  opts?: ReadonlyListItemOpts,
): ReadonlyListItem {
  return {
    id,
    title: label,
    value: { currency: { value, ...formatOptions } },
    ...opts,
  };
}

export function getReadonlyListItemAsset(
  id: string,
  label: ReactNode,
  value: number | string | undefined,
  asset: IAssetDisplay | undefined,
  formatOptions?: Omit<AssetFormatProps, 'asset' | 'amount'>,
  opts?: ReadonlyListItemOpts,
): ReadonlyListItem {
  return {
    id,
    title: label,
    value: { asset: { amount: value, asset, ...formatOptions } },
    ...opts,
  };
}

export function getReadonlyListItemTranslatedString<TNamespace extends I18nNamespaces>(
  id: string,
  label: ReactNode,
  value: I18nKey<TNamespace>,
  variables?: any,
  opts?: ReadonlyListItemOpts,
): ReadonlyListItem {
  const baseItem = {
    id,
    title: label,
    ...opts,
  } as const;

  if (!value || (typeof value === 'object' && !value.value)) {
    return {
      ...baseItem,
      value: { string: { value: undefined } },
    } as const;
  }

  if (typeof value === 'string') {
    return {
      ...baseItem,
      value: {
        node: {
          value: <TranslatedText<TNamespace> i18nKey={value} variables={variables} />,
        },
      },
    } as const;
  }

  return {
    ...baseItem,
    value: {
      node: {
        value: <TranslatedText<TNamespace> defaultValue={value.value} i18nKey={value} variables={variables} />,
      },
    },
  } as const;
}

export function getReadonlyListItemJson(
  id: string,
  label: string,
  value: Record<string, any> | undefined,
  opts?: ReadonlyListItemOpts,
): ReadonlyListItem {
  const jsonString = value ? JSON.stringify(value, null, 2) : undefined;

  return getReadonlyListItemNode(
    id,
    label,
    jsonString ? <CodeEditorInput disabled hideLabel label={label} language="json" value={jsonString} /> : null,
    {
      columns: jsonString ? 1 : undefined,
      valueTypography: { ...defaultValueStackedTypographyProps, tone: 'md', style: { width: '100%' } },
      ...opts,
    },
  );
}

type LinkPrefixOrBuilder = string | ((id: string) => string | undefined);

function IdListNode({ ids, linkPrefixOrBuilder }: { ids: string[]; linkPrefixOrBuilder: LinkPrefixOrBuilder | undefined }) {
  if (!ids.length) {
    return null;
  }

  return (
    <span>
      {ids.map((id, i) => {
        let linkTo: string | undefined;

        if (typeof linkPrefixOrBuilder === 'function') {
          linkTo = linkPrefixOrBuilder(id);
        } else if (typeof linkPrefixOrBuilder === 'string') {
          linkTo = `${linkPrefixOrBuilder}${id}`;
        }

        return (
          <span key={id}>
            <ID canCopy={false} inline LinkElement={Link} to={linkTo} value={id} />
            {i === ids.length - 1 ? '' : ', '}
          </span>
        );
      })}
    </span>
  );
}

export function getReadonlyListIdList(
  id: string,
  label: ReactNode,
  value: string[] | undefined,
  linkPrefixOrBuilder?: LinkPrefixOrBuilder,
  opts?: ReadonlyListItemOpts,
) {
  return getReadonlyListItemNode(id, label, value?.length ? <IdListNode ids={value} linkPrefixOrBuilder={linkPrefixOrBuilder} /> : null, opts);
}
